import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { centralApi } from '@/services/centralApi';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';

export default function CentralBaseConhecimento() {
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filtro, setFiltro] = useState('all');
  const [form, setForm] = useState({ titulo: '', categoria: 'Empresa', conteudo: '' });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const d = await centralApi.listKnowledge(filtro !== 'all' ? filtro : undefined);
    setItems(d.items || []);
    setCategorias(d.categorias || []);
    if (!form.categoria && d.categorias?.length) setForm(f => ({ ...f, categoria: d.categorias[0] }));
  };
  useEffect(() => { load(); }, [filtro]);

  const salvar = async () => {
    if (!form.titulo.trim() || !form.conteudo.trim()) return toast.error('Preencha título e conteúdo');
    try {
      if (editingId) {
        await centralApi.updateKnowledge(editingId, form);
        toast.success('Item atualizado');
      } else {
        await centralApi.createKnowledge(form);
        toast.success('Item adicionado');
      }
      setForm({ titulo: '', categoria: form.categoria, conteudo: '' });
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remover = async (id) => {
    if (!window.confirm('Excluir este item?')) return;
    await centralApi.deleteKnowledge(id);
    load();
  };

  const toggleAtivo = async (item) => {
    await centralApi.updateKnowledge(item.id, { ativo: !item.ativo });
    load();
  };

  const editar = (item) => {
    setForm({ titulo: item.titulo, categoria: item.categoria, conteudo: item.conteudo });
    setEditingId(item.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Layout>
    <div className="space-y-4" data-testid="central-base-conhecimento">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Base de Conhecimento</h1>
        <p className="text-sm text-muted-foreground">Informações da empresa usadas pela assistente virtual</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <Select value={filtro} onValueChange={setFiltro}>
            <SelectTrigger className="w-full sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as categorias</SelectItem>
              {categorias.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {items.length === 0 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground border-dashed">
              Nenhum item cadastrado. Adicione informações reais no formulário ao lado.
            </Card>
          ) : items.map(item => (
            <Card key={item.id} className="p-4">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{item.titulo}</p>
                  <span className="inline-block mt-1 text-[10px] uppercase tracking-wider bg-muted px-2 py-0.5 rounded font-semibold text-muted-foreground">
                    {item.categoria}
                  </span>
                  <p className="text-sm mt-2 text-muted-foreground whitespace-pre-wrap">{item.conteudo}</p>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Atualizado por {item.updated_by_name || '—'} · {new Date(item.updated_at).toLocaleString('pt-BR')}
                  </p>
                </div>
                <div className="flex flex-col gap-2 items-end shrink-0">
                  <div className="flex items-center gap-1 text-xs">
                    <Switch checked={item.ativo} onCheckedChange={() => toggleAtivo(item)} />
                    <span>{item.ativo ? 'Ativo' : 'Inativo'}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => editar(item)}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => remover(item.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Card className="border-border h-fit">
          <CardHeader><CardTitle className="text-base">{editingId ? 'Editar item' : 'Novo item'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs font-medium">Título</label>
              <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} data-testid="kb-titulo" />
            </div>
            <div>
              <label className="text-xs font-medium">Categoria</label>
              <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                <SelectTrigger data-testid="kb-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>{categorias.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Conteúdo</label>
              <Textarea rows={6} value={form.conteudo} onChange={(e) => setForm({ ...form, conteudo: e.target.value })} data-testid="kb-conteudo" />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={salvar} data-testid="kb-save">{editingId ? 'Salvar' : 'Adicionar'}</Button>
              {editingId && <Button variant="outline" onClick={() => { setEditingId(null); setForm({ titulo: '', categoria: form.categoria, conteudo: '' }); }}>Cancelar</Button>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </Layout>
  );
}
